import { Injectable } from '@angular/core'

import { HttpService } from '../http.service'
import { ADMIN_API_ENDPOINTS } from '../../common/api-endpoints'
import { MallDto, PageResultDto } from '@api/dto'
import { TranslationDto } from '@api/dto/translation.dto'
import {
  FloorDto,
  TableDto,
  CreateTableDto,
  UpdateTableDto,
  FindTableDto,
} from '@api/dto/governance-admin'
import { FloorApiService } from './floor.service'

const MALLS_PATH = '/admin/v1/malls'

@Injectable({
  providedIn: 'root',
})
export class GovernTableApiService {
  constructor(
    private readonly httpService: HttpService,
    private readonly floorApiService: FloorApiService
  ) {}

  // ===========================================================================
  // Mall & Floor (delegate to existing APIs)
  // ===========================================================================

  public async getMalls(): Promise<MallDto[]> {
    try {
      const result = await this.httpService.get<PageResultDto<MallDto>>(
        MALLS_PATH,
        { limit: 100, offset: 0 }
      )
      return result?.rows ?? []
    } catch {
      return []
    }
  }

  public async getFloorsByMall(mallId: string): Promise<FloorDto[]> {
    try {
      const res = await this.floorApiService.getFloors({ mallId })
      return res?.rows ?? []
    } catch {
      return []
    }
  }

  // ===========================================================================
  // Tables CRUD (FNB_TABLE API)
  // ===========================================================================

  public async find(
    findTableDto: FindTableDto = {}
  ): Promise<{ rows: TableDto[]; count: number }> {
    const params: Record<string, string | number | undefined> = {}
    if (findTableDto.limit != null) params.limit = findTableDto.limit
    if (findTableDto.offset != null) params.offset = findTableDto.offset
    if (findTableDto.mallId) params.mallId = findTableDto.mallId
    if (findTableDto.floorId) params.floorId = findTableDto.floorId
    if (findTableDto.searchText) params.searchText = findTableDto.searchText
    if (findTableDto.enabled != null) params.enabled = findTableDto.enabled

    const result = await this.httpService.get<PageResultDto<TableDto>>(
      ADMIN_API_ENDPOINTS.FNB_TABLE,
      params
    )
    return {
      rows: result?.rows ?? [],
      count: result?.count ?? 0,
    }
  }

  public async findOne(id: string): Promise<TableDto | null> {
    try {
      const table = await this.httpService.get<TableDto>(
        `${ADMIN_API_ENDPOINTS.FNB_TABLE}/${id}`
      )
      return table ?? null
    } catch {
      return null
    }
  }

  public async create(createDto: CreateTableDto): Promise<TableDto> {
    const body = {
      name: typeof createDto.name === 'string' ? createDto.name : String(createDto.name ?? ''),
      enabled: createDto.enabled,
      mallId: createDto.mallId,
      floorId: createDto.floorId,
    }
    const created = await this.httpService.post<TableDto>(
      ADMIN_API_ENDPOINTS.FNB_TABLE,
      body
    )
    return this.normalizeTableDto(created)
  }

  public async update(
    id: string,
    updateDto: UpdateTableDto
  ): Promise<TableDto> {
    const body: Record<string, unknown> = {}
    if (updateDto.name != null) {
      body.name =
        typeof updateDto.name === 'string'
          ? updateDto.name
          : String(updateDto.name ?? '')
    }
    if (updateDto.enabled !== undefined) body.enabled = updateDto.enabled
    if (updateDto.mallId !== undefined) body.mallId = updateDto.mallId
    if (updateDto.floorId !== undefined) body.floorId = updateDto.floorId

    const updated = await this.httpService.put<TableDto>(
      `${ADMIN_API_ENDPOINTS.FNB_TABLE}/${id}`,
      body
    )
    return this.normalizeTableDto(updated)
  }

  public async setEnable(id: string, enabled: boolean): Promise<TableDto | null> {
    return this.update(id, { enabled })
  }

  private normalizeTableDto(raw: TableDto | undefined): TableDto {
    if (!raw) return TableDto.create({})
    const name =
      raw.name && typeof raw.name === 'object' && 'en' in raw.name
        ? raw.name
        : TranslationDto.create(
            typeof raw.name === 'string' ? raw.name : '',
            ''
          )
    return TableDto.create({
      id: raw.id,
      name,
      enabled: raw.enabled,
      mall: raw.mall,
      floor: raw.floor,
      code: raw.code,
      deeplink: raw.deeplink,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    })
  }
}
